/* =====================================================================
 * emfToSvg — 최소 EMF(Enhanced Metafile) → SVG 변환기
 *
 * 브라우저는 EMF 를 디코드하지 못해 PPTX 안의 벡터 클립아트(곡선 화살표 등)가
 * 자리표시자로만 렌더된다(roadmaps.pptx 실측). PowerPoint 가 만드는 클립아트
 * EMF 는 대부분 "경로 구성(POLY*16/MOVETOEX) → 페인트(STROKE/FILL PATH)"의
 * 좁은 레코드 집합이라, 그 부분집합만 SVG <path> 로 옮긴다.
 *
 * 지원 레코드 밖(비트맵/텍스트 등)이 그리기에 관여하면 null 을 반환해
 * 호출부가 기존 자리표시자 폴백을 유지한다. WMF 는 다른 포맷 — 미지원.
 * 참조: MS-EMF 스펙의 레코드 레이아웃.
 * ===================================================================== */

interface EmfObject {
  kind: 'pen' | 'brush' | 'null';
  color?: string;   // css color
  width?: number;   // pen only (장치 단위)
}

interface WorldXform {
  m11: number; m12: number; m21: number; m22: number; dx: number; dy: number;
}

const IDENTITY: WorldXform = { m11: 1, m12: 0, m21: 0, m22: 1, dx: 0, dy: 0 };

/** COLORREF(0x00BBGGRR) → #RRGGBB */
function colorRef(v: number): string {
  const r = v & 0xff;
  const g = (v >> 8) & 0xff;
  const b = (v >> 16) & 0xff;
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`;
}

/** GDI 스톡 오브젝트(0x80000000 | idx) 중 렌더에 필요한 것만 해석 */
function stockObject(idx: number): EmfObject {
  switch (idx) {
    case 0x00: return { kind: 'brush', color: '#FFFFFF' }; // WHITE_BRUSH
    case 0x04: return { kind: 'brush', color: '#000000' }; // BLACK_BRUSH
    case 0x05: return { kind: 'null' };                    // NULL_BRUSH
    case 0x06: return { kind: 'pen', color: '#FFFFFF', width: 1 }; // WHITE_PEN
    case 0x07: return { kind: 'pen', color: '#000000', width: 1 }; // BLACK_PEN
    case 0x08: return { kind: 'null' };                    // NULL_PEN
    default: return { kind: 'null' };
  }
}

function fmtN(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/**
 * EMF 바이트를 SVG 마크업 문자열로 변환한다. 지원 범위를 벗어나는
 * "그리기" 레코드를 만나면 null (호출부는 자리표시자 폴백).
 */
export function emfToSvg(buffer: ArrayBuffer): string | null {
  const dv = new DataView(buffer);
  if (buffer.byteLength < 88) return null;
  // EMR_HEADER: iType=1, rclBounds(장치 단위 LTRB) @8..24
  if (dv.getUint32(0, true) !== 1) return null;
  const bl = dv.getInt32(8, true);
  const bt = dv.getInt32(12, true);
  const br = dv.getInt32(16, true);
  const bb = dv.getInt32(20, true);
  const vbW = br - bl;
  const vbH = bb - bt;
  if (vbW <= 0 || vbH <= 0) return null;

  const objects = new Map<number, EmfObject>();
  let pen: EmfObject = { kind: 'pen', color: '#000000', width: 1 };
  let brush: EmfObject = { kind: 'brush', color: '#000000' };
  let xform: WorldXform = { ...IDENTITY };
  let fillRule: 'evenodd' | 'nonzero' = 'evenodd';

  let inPath = false;
  let pathD = '';
  let cur = { x: 0, y: 0 };
  const out: string[] = [];

  const tp = (x: number, y: number): { x: number; y: number } => ({
    x: x * xform.m11 + y * xform.m21 + xform.dx,
    y: x * xform.m12 + y * xform.m22 + xform.dy,
  });

  const emit = (d: string, doFill: boolean, doStroke: boolean): void => {
    if (!d) return;
    const fill = doFill && brush.kind === 'brush' && brush.color ? brush.color : 'none';
    const stroke = doStroke && pen.kind === 'pen' && pen.color ? pen.color : 'none';
    if (fill === 'none' && stroke === 'none') return;
    const sw = doStroke && pen.width ? Math.max(pen.width, 1) : 0;
    out.push(
      `<path d="${d}" fill="${fill}" fill-rule="${fillRule}" stroke="${stroke}"`
      + (sw > 0 ? ` stroke-width="${fmtN(sw)}"` : '')
      + ' stroke-linecap="round" stroke-linejoin="round"/>',
    );
  };

  // 16/32비트 폴리 계열 공용 판독기 — to(현재 경로에 이어붙임)/독립 도형 모두 커버
  const readPoly = (
    off: number,
    is16: boolean,
    mode: 'lineTo' | 'bezTo' | 'line' | 'polygon' | 'bezier',
  ): boolean => {
    const cpts = dv.getUint32(off + 24, true);
    let p = off + 28;
    const step = is16 ? 4 : 8;
    if (p + cpts * step > buffer.byteLength) return false;
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < cpts; i++) {
      const x = is16 ? dv.getInt16(p, true) : dv.getInt32(p, true);
      const y = is16 ? dv.getInt16(p + (is16 ? 2 : 4), true) : dv.getInt32(p + 4, true);
      pts.push(tp(x, y));
      p += step;
    }
    if (pts.length === 0) return true;

    const seg = (list: { x: number; y: number }[], asBez: boolean, startWithMove: boolean): string => {
      let d = startWithMove ? `M${fmtN(list[0].x)},${fmtN(list[0].y)} ` : '';
      const body = startWithMove ? list.slice(1) : list;
      if (asBez) {
        for (let i = 0; i + 2 < body.length; i += 3) {
          const c1 = body[i];
          const c2 = body[i + 1];
          const e = body[i + 2];
          d += `C${fmtN(c1.x)},${fmtN(c1.y)} ${fmtN(c2.x)},${fmtN(c2.y)} ${fmtN(e.x)},${fmtN(e.y)} `;
        }
      } else {
        for (const pt of body) d += `L${fmtN(pt.x)},${fmtN(pt.y)} `;
      }
      return d;
    };

    switch (mode) {
      case 'lineTo':
      case 'bezTo': {
        const d = seg(pts, mode === 'bezTo', false);
        if (inPath) pathD += d;
        cur = pts[pts.length - 1];
        break;
      }
      case 'line':
      case 'bezier': {
        const d = seg(pts, mode === 'bezier', true);
        if (inPath) pathD += d;
        else emit(d, false, true);
        cur = pts[pts.length - 1];
        break;
      }
      case 'polygon': {
        const d = `${seg(pts, false, true)}Z`;
        if (inPath) pathD += d;
        else emit(d, true, true);
        cur = pts[pts.length - 1];
        break;
      }
    }
    return true;
  };

  let off = 0;
  while (off + 8 <= buffer.byteLength) {
    const type = dv.getUint32(off, true);
    const size = dv.getUint32(off + 4, true);
    if (size < 8 || off + size > buffer.byteLength) return null;

    switch (type) {
      case 1: break; // HEADER (이미 처리)
      case 14: // EOF
        off = buffer.byteLength;
        continue;

      case 27: { // MOVETOEX
        const p = tp(dv.getInt32(off + 8, true), dv.getInt32(off + 12, true));
        if (inPath) pathD += `M${fmtN(p.x)},${fmtN(p.y)} `;
        cur = p;
        break;
      }
      case 54: { // LINETO
        const p = tp(dv.getInt32(off + 8, true), dv.getInt32(off + 12, true));
        if (inPath) pathD += `L${fmtN(p.x)},${fmtN(p.y)} `;
        else emit(`M${fmtN(cur.x)},${fmtN(cur.y)} L${fmtN(p.x)},${fmtN(p.y)}`, false, true);
        cur = p;
        break;
      }

      case 85: if (!readPoly(off, true, 'bezier')) return null; break;  // POLYBEZIER16
      case 86: if (!readPoly(off, true, 'polygon')) return null; break; // POLYGON16
      case 87: if (!readPoly(off, true, 'line')) return null; break;    // POLYLINE16
      case 88: if (!readPoly(off, true, 'bezTo')) return null; break;   // POLYBEZIERTO16
      case 89: if (!readPoly(off, true, 'lineTo')) return null; break;  // POLYLINETO16
      case 2: if (!readPoly(off, false, 'bezier')) return null; break;  // POLYBEZIER
      case 3: if (!readPoly(off, false, 'polygon')) return null; break; // POLYGON
      case 4: if (!readPoly(off, false, 'line')) return null; break;    // POLYLINE
      case 5: if (!readPoly(off, false, 'bezTo')) return null; break;   // POLYBEZIERTO
      case 6: if (!readPoly(off, false, 'lineTo')) return null; break;  // POLYLINETO

      case 59: inPath = true; pathD = ''; break;  // BEGINPATH
      case 60: break;                             // ENDPATH (pathD 유지)
      case 61: if (inPath) pathD += 'Z '; break;  // CLOSEFIGURE
      case 62: emit(pathD, true, false); inPath = false; pathD = ''; break; // FILLPATH
      case 63: emit(pathD, true, true); inPath = false; pathD = ''; break;  // STROKEANDFILLPATH
      case 64: emit(pathD, false, true); inPath = false; pathD = ''; break; // STROKEPATH

      case 19: // SETPOLYFILLMODE (1=ALTERNATE→evenodd, 2=WINDING→nonzero)
        fillRule = dv.getUint32(off + 8, true) === 2 ? 'nonzero' : 'evenodd';
        break;

      case 35: { // SETWORLDTRANSFORM
        xform = {
          m11: dv.getFloat32(off + 8, true), m12: dv.getFloat32(off + 12, true),
          m21: dv.getFloat32(off + 16, true), m22: dv.getFloat32(off + 20, true),
          dx: dv.getFloat32(off + 24, true), dy: dv.getFloat32(off + 28, true),
        };
        break;
      }
      case 36: { // MODIFYWORLDTRANSFORM
        const m: WorldXform = {
          m11: dv.getFloat32(off + 8, true), m12: dv.getFloat32(off + 12, true),
          m21: dv.getFloat32(off + 16, true), m22: dv.getFloat32(off + 20, true),
          dx: dv.getFloat32(off + 24, true), dy: dv.getFloat32(off + 28, true),
        };
        const mode = dv.getUint32(off + 32, true);
        if (mode === 1) xform = { ...IDENTITY };            // MWT_IDENTITY
        else if (mode === 4) xform = m;                     // MWT_SET
        else {
          // MWT_LEFTMULTIPLY(2)/RIGHTMULTIPLY(3) — 순서 근사: 새 변환을 기존에 합성
          xform = {
            m11: m.m11 * xform.m11 + m.m12 * xform.m21,
            m12: m.m11 * xform.m12 + m.m12 * xform.m22,
            m21: m.m21 * xform.m11 + m.m22 * xform.m21,
            m22: m.m21 * xform.m12 + m.m22 * xform.m22,
            dx: m.dx * xform.m11 + m.dy * xform.m21 + xform.dx,
            dy: m.dx * xform.m12 + m.dy * xform.m22 + xform.dy,
          };
        }
        break;
      }

      case 37: { // SELECTOBJECT
        const ih = dv.getUint32(off + 8, true);
        const obj = (ih & 0x80000000) ? stockObject(ih & 0x7fffffff) : objects.get(ih);
        if (obj) {
          if (obj.kind === 'pen') pen = obj;
          else if (obj.kind === 'brush') brush = obj;
          else {
            // NULL 오브젝트 — 어느 슬롯인지 스톡 인덱스로 판정
            if ((ih & 0x7fffffff) === 0x08) pen = obj;
            else brush = obj;
          }
        }
        break;
      }
      case 38: { // CREATEPEN: ihPen, LogPen{style, width(POINTL), color}
        const ih = dv.getUint32(off + 8, true);
        objects.set(ih, {
          kind: 'pen',
          width: dv.getInt32(off + 16, true) || 1,
          color: colorRef(dv.getUint32(off + 24, true)),
        });
        break;
      }
      case 95: { // EXTCREATEPEN: ihPen@8, offBmi/cbBmi/offBits/cbBits@12..28, elp{style,width,brushStyle,color}@28
        const ih = dv.getUint32(off + 8, true);
        objects.set(ih, {
          kind: 'pen',
          width: dv.getUint32(off + 32, true) || 1,
          color: colorRef(dv.getUint32(off + 40, true)),
        });
        break;
      }
      case 39: { // CREATEBRUSHINDIRECT: ihBrush@8, LogBrushEx{style@12, color@16, hatch}
        const ih = dv.getUint32(off + 8, true);
        const style = dv.getUint32(off + 12, true);
        objects.set(ih, style === 1
          ? { kind: 'null' } // BS_NULL
          : { kind: 'brush', color: colorRef(dv.getUint32(off + 16, true)) });
        break;
      }
      case 40: objects.delete(dv.getUint32(off + 8, true)); break; // DELETEOBJECT

      // 상태/무해 레코드 — 그리기에 영향 없다고 보고 스킵
      case 9: case 10: case 11: case 12: case 17: case 18: case 20: case 21:
      case 22: case 24: case 25: case 28: case 29: case 30: case 33: case 34:
      case 48: case 58: case 65: case 67: case 68: case 70: case 76: case 98:
        break;

      default:
        // 미지원 "그리기" 레코드(텍스트/비트맵/영역 등) → 불완전 렌더보다 폴백
        return null;
    }
    off += size;
  }

  if (out.length === 0) return null;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${bl} ${bt} ${vbW} ${vbH}">${out.join('')}</svg>`;
}
